import Countdown, { zeroPad } from 'react-countdown';
import styled from 'styled-components';
import { ICountdownProps, ICountdownRendererProps } from 'shared/types/Countdown';
import { t } from 'localization/utils/t';

const TP = 'shared.components.Countdown';

// Renderer callback with condition
const CountdownRenderer = ({
  enableClockStyle = false,
  formatted: { days, hours, minutes, seconds },
  isLowercase,
  hideDaysWhenZero,
  transitionToRed,
  className,
}: ICountdownRendererProps) => {
  const [daysNum, hoursNum] = [Number.parseInt(days, 10) || 0, Number.parseInt(hours, 10) || 0];
  const hideDays = hideDaysWhenZero && daysNum <= 0;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const hideHours = hideDays && hours <= 0;
  const redText = transitionToRed && daysNum <= 0 && hoursNum < 24;

  const daysWithColon = hideDays
    ? ''
    : t(`${TP}.countdown1Days`, '{days}:', { days: zeroPad(days) });
  const hoursWithColon = hideHours ? '' : t(`${TP}.countdown1Hours`, '{hours}:', { hours });
  const daysWithWord = hideDays
    ? ''
    : t(`${TP}.countdown2Days`, '{days}D ', { days: zeroPad(days) });

  return enableClockStyle ? (
    <CountdownRenderer.CountdownContainer
      suppressHydrationWarning
      data-qa="countdown"
      className={className}
      $redText={redText}
      $isLowercase={isLowercase}
    >
      {t(`${TP}.countdown1`, '{days}{hours}{minutes}:{seconds}', {
        days: daysWithColon,
        hours: hoursWithColon,
        minutes,
        seconds,
      })}
    </CountdownRenderer.CountdownContainer>
  ) : (
    <CountdownRenderer.CountdownContainer
      suppressHydrationWarning
      data-qa="countdown"
      className={className}
      $redText={redText}
      $isLowercase={isLowercase}
    >
      {t(`${TP}.countdown2`, '{days}{hours}H {minutes}M {seconds}S', {
        days: daysWithWord,
        hours,
        minutes,
        seconds,
      })}
    </CountdownRenderer.CountdownContainer>
  );
};

/**
 * [CountdownComponent]
 * Renders a countdown in the format 00d 00h 00m 00s.
 * @param {boolean} active - Does the countdown start automatically?
 * @param {string | number | Date} date - How long from the current time should it countdown to?
 * @param {Function | undefined} - can be passed to countdown component to monitor countdown progress
 * @param {boolean} hideDaysWhenZero - Should the days be hidden if the `# of days` = 0?
 * @param {boolean} hoursOnly - Show days in hours?
 * @param {boolean} isLowercase - Use lower case D, H, M, S?
 * @param {boolean} transitionToRed - Should the countdown turn red during the last 24 hours?
 */
export const CountdownComponent = ({
  active = true,
  countdownRef,
  enableClockStyle = false,
  date,
  handleTick,
  hoursOnly = false,
  hideDaysWhenZero = false,
  isLowercase = false,
  transitionToRed = false,
  className,
}: ICountdownProps) => {
  return (
    <Countdown
      autoStart={active}
      date={date}
      daysInHours={hoursOnly}
      onTick={handleTick}
      ref={countdownRef}
      renderer={(props) => (
        <CountdownRenderer
          enableClockStyle={enableClockStyle}
          isLowercase={isLowercase}
          hideDaysWhenZero={hideDaysWhenZero || hoursOnly}
          transitionToRed={transitionToRed}
          className={className}
          {...props}
        />
      )}
    />
  );
};

CountdownRenderer.CountdownContainer = styled.span<{ $redText: boolean; $isLowercase: boolean }>`
  display: block;
  white-space: nowrap;
  ${({ $isLowercase }) => $isLowercase && 'text-transform: lowercase'};
  color: ${({ theme, $redText }) =>
    $redText ? theme.colors.red : theme.navigation.textColor || 'black'};
`;
